import {
    DateFilterScope,
    DateParcelFilterScope,
    MultipleOptionStringKeywordFilterScope,
    MultipleOptionStringKeywordParcelFilterScope,
    NumberFilterScope,
    NumberParcelFilterScope,
    SelectFilterScope,
    SelectParcelFilterScope,
    StringFilterScope,
    StringKeywordFilterScope,
    StringKeywordParcelFilterScope,
    StringParcelFilterScope,
    type AppliedFilterPart,
    type AppliedFilterSummary,
    type IFilterScope
} from './tcm_scopes'
import { NumberOperator, SelectOperator, StringKeywordOperator, StringOperator } from './tcm_service'

const MAX_TAGS = 3
const MAX_CHARS = 24

const numberFormat = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 })

export function summarizeFilterScope(scope: IFilterScope): AppliedFilterSummary | undefined {
    const parts: AppliedFilterPart[] = []

    if (scope instanceof StringFilterScope) {
        collect(scope.conjunction, scope.disjunction, parts, describeStringParcel)
    } else if (scope instanceof StringKeywordFilterScope) {
        collect(scope.conjunction, scope.disjunction, parts, describeStringKeywordParcel)
    } else if (scope instanceof MultipleOptionStringKeywordFilterScope) {
        collect(scope.conjunction, scope.disjunction, parts, describeMultipleParcel)
    } else if (scope instanceof NumberFilterScope) {
        collect(scope.conjunction, scope.disjunction, parts, describeNumberParcel)
    } else if (scope instanceof DateFilterScope) {
        collect(scope.conjunction, scope.disjunction, parts, describeDateParcel)
    } else if (scope instanceof SelectFilterScope) {
        const options = scope.getOptions()
        collect(scope.conjunction, scope.disjunction, parts, (parcel: SelectParcelFilterScope) =>
            describeSelectParcel(parcel, options)
        )
    }

    return parts.length > 0 ? { caption: scope.caption, parts } : undefined
}

export function buildSummary(caption: string, values: string[]): AppliedFilterSummary | undefined {
    const part = toPart(values)
    return part ? { caption, parts: [part] } : undefined
}

function collect<S>(
    conjunction: Iterable<S>,
    disjunction: Iterable<S>,
    target: AppliedFilterPart[],
    describe: (parcel: S) => AppliedFilterPart | undefined
) {
    for (const parcel of [...conjunction, ...disjunction]) {
        const part = describe(parcel)
        part && target.push(part)
    }
}

function toPart(values: string[], prefix?: string): AppliedFilterPart | undefined {
    const cleaned = values.map((value) => value.trim()).filter((value) => value.length > 0)
    if (cleaned.length === 0) {
        return undefined
    }

    const tags: string[] = []
    let chars = 0
    for (const value of cleaned) {
        if (tags.length > 0 && (tags.length >= MAX_TAGS || chars + value.length > MAX_CHARS)) {
            break
        }
        tags.push(value)
        chars += value.length
    }

    return { prefix, tags, hiddenCount: cleaned.length - tags.length }
}

function blankLabel(operator: number): string | undefined {
    switch (operator) {
        case StringOperator.is_blank:
            return 'em branco'
        case StringOperator.is_not_blank:
            return 'preenchido'
        case StringOperator.is_empty:
            return 'vazio'
        case StringOperator.is_not_empty:
            return 'não vazio'
        default:
            return undefined
    }
}

function textPrefix(operator: number): string | undefined {
    switch (operator) {
        case StringOperator.does_not_contain:
            return 'não contém'
        case StringOperator.starts_with:
            return 'começa com'
        case StringOperator.does_not_start_with:
            return 'não começa com'
        case StringOperator.is_not:
            return 'diferente de'
        case StringOperator.is_not_in:
            return 'fora da lista'
        default:
            return undefined
    }
}

function isListOperator(operator: number) {
    return operator === StringKeywordOperator.is_in || operator === StringKeywordOperator.is_not_in
}

function describeStringParcel(parcel: StringParcelFilterScope): AppliedFilterPart | undefined {
    const blank = blankLabel(parcel.operator)
    if (blank) {
        return { tags: [blank], hiddenCount: 0 }
    }

    const value = parcel.value.trim()
    if (!value) {
        return undefined
    }

    const values = isListOperator(parcel.operator) ? splitValues(value) : [value]
    return toPart(values, textPrefix(parcel.operator))
}

function describeStringKeywordParcel(parcel: StringKeywordParcelFilterScope): AppliedFilterPart | undefined {
    const blank = blankLabel(parcel.operator)
    if (blank) {
        return { tags: [blank], hiddenCount: 0 }
    }

    const value = parcel.value.trim()
    if (!value) {
        return undefined
    }

    const values = isListOperator(parcel.operator) ? splitValues(value) : [value]
    return toPart(
        values.map((item) => labelOf(parcel.options, item)),
        textPrefix(parcel.operator)
    )
}

function describeMultipleParcel(parcel: MultipleOptionStringKeywordParcelFilterScope): AppliedFilterPart | undefined {
    const blank = blankLabel(parcel.operator)
    if (blank) {
        return { tags: [blank], hiddenCount: 0 }
    }

    if (parcel.value.length === 0) {
        return undefined
    }

    const labels = new Map(parcel.options.map((option) => [option.key, option.label]))
    return toPart(
        parcel.value.map((key) => labels.get(key) ?? key),
        textPrefix(parcel.operator)
    )
}

function describeNumberParcel(parcel: NumberParcelFilterScope): AppliedFilterPart | undefined {
    if (parcel.operator === NumberOperator.is_blank) {
        return { tags: ['em branco'], hiddenCount: 0 }
    }

    if (parcel.operator === NumberOperator.is_not_blank) {
        return { tags: ['preenchido'], hiddenCount: 0 }
    }

    const from = formatNumber(parcel.value)
    const to = formatNumber(parcel.toValue)

    if (parcel.operator === NumberOperator.in_range || parcel.operator === NumberOperator.out_of_range) {
        const range = describeRange(from, to)
        if (!range) {
            return undefined
        }
        return {
            prefix: parcel.operator === NumberOperator.out_of_range ? 'fora de' : undefined,
            tags: [range],
            hiddenCount: 0
        }
    }

    if (!from) {
        return undefined
    }

    switch (parcel.operator) {
        case NumberOperator.is_less_then:
            return { tags: [`< ${from}`], hiddenCount: 0 }
        case NumberOperator.is_less_then_or_equal_to:
            return { tags: [`≤ ${from}`], hiddenCount: 0 }
        case NumberOperator.is_greater_then:
            return { tags: [`> ${from}`], hiddenCount: 0 }
        case NumberOperator.is_greater_then_or_equal_to:
            return { tags: [`≥ ${from}`], hiddenCount: 0 }
        case NumberOperator.is_not:
            return { prefix: 'diferente de', tags: [from], hiddenCount: 0 }
        default:
            return { tags: [from], hiddenCount: 0 }
    }
}

function describeDateParcel(parcel: DateParcelFilterScope): AppliedFilterPart | undefined {
    const range = describeRange(formatDate(parcel.value), formatDate(parcel.toValue))
    return range ? { tags: [range], hiddenCount: 0 } : undefined
}

function describeSelectParcel(
    parcel: SelectParcelFilterScope,
    options: Array<{ id: string; title: string }>
): AppliedFilterPart | undefined {
    const blank = blankLabel(parcel.operator)
    if (blank) {
        return { tags: [blank], hiddenCount: 0 }
    }

    const value = `${parcel.value ?? ''}`.trim()
    if (!value) {
        return undefined
    }

    const title = options.find((option) => `${option.id}` === value)?.title ?? value
    return {
        prefix: parcel.operator === SelectOperator.is_not ? 'diferente de' : undefined,
        tags: [title],
        hiddenCount: 0
    }
}

function splitValues(value: string) {
    return value
        .split(/[,;\n]/)
        .map((item) => item.trim())
        .filter((item) => item.length > 0)
}

function labelOf(options: Array<{ key: string; label: string }>, key: string) {
    return options.find((option) => option.key === key)?.label ?? key
}

function describeRange(from: string, to: string) {
    if (from && to) {
        return `${from} a ${to}`
    }
    if (from) {
        return `a partir de ${from}`
    }
    if (to) {
        return `até ${to}`
    }
    return ''
}

function formatNumber(value: string | number | undefined) {
    const text = `${value ?? ''}`.trim()
    if (!text) {
        return ''
    }

    const parsed = Number(text.replace(',', '.'))
    return Number.isFinite(parsed) ? numberFormat.format(parsed) : text
}

function formatDate(value: string | undefined) {
    const text = `${value ?? ''}`.trim()
    const parts = /^(\d{4})-(\d{2})-(\d{2})/.exec(text)
    return parts ? `${parts[3]}/${parts[2]}/${parts[1]}` : text
}
